import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { lightLoadingOverlay } from '@/common/LoadingOverlay';
import { SectionHeader } from '@/vdp/styles';
import Sections from '~/constants/sections';
import { useIsMobile } from '~/context/common/DeviceContext';
import VehiclesAvailableSoonerContext from '~/context/vdp/VehiclesAvailableSoonerContext';
import { useGetVehiclesAvailableSooner } from '~/hooks/vdp/services';
import {
  getDeliveryPickup,
  getIsConsignment,
  getIsPartnerVehicle,
  getShouldHideDeliveryPickup
} from '~/selectors';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';

import {
  InnerWrapper,
  Section,
  DisclaimerText
} from './EarliestDelivery.styles';
import PickupAndDelivery from './PickupAndDelivery';
import Shipping from './Shipping';
import SimilarVehicles from './SimilarVehicles';

export enum EarliestDeliveryState {
  MobileCars,
  MobileNoCars,
  DesktopCars,
  DesktopNoCars
}

const EarliestDelivery = () => {
  const { data: vehicles, isLoaded } = useContext(
    VehiclesAvailableSoonerContext
  );
  // We use !isLoaded instead of isLoading because isLoading is false by default, so it may lead to layout shifts
  const vehiclesAreLoading = !isLoaded;
  const isMobile = useIsMobile();
  const isPartner = useSelector(getIsPartnerVehicle);
  const isConsignment = useSelector(getIsConsignment);
  const [componentState, setState] = useState<EarliestDeliveryState>(
    isMobile
      ? EarliestDeliveryState.MobileNoCars
      : EarliestDeliveryState.DesktopNoCars
  );
  const hideSection = useSelector(getShouldHideDeliveryPickup);
  const { isLoaded: deliveryPickupIsLoaded } = useSelector(getDeliveryPickup);
  const showLoader = vehiclesAreLoading || !deliveryPickupIsLoaded;

  useGetVehiclesAvailableSooner(!hideSection);

  const hasCars = Boolean(vehicles.length);

  useEffect(() => {
    switch (true) {
      case isMobile && hasCars:
        setState(EarliestDeliveryState.MobileCars);
        break;
      case isMobile && !hasCars:
        setState(EarliestDeliveryState.MobileNoCars);
        break;
      case hasCars:
        setState(EarliestDeliveryState.DesktopCars);
        break;
      default:
        setState(EarliestDeliveryState.DesktopNoCars);
        break;
    }
  }, [hasCars, isMobile]);

  if (hideSection) return null;

  return (
    <Section
      className={showLoader ? lightLoadingOverlay : undefined}
      id={Sections.EarliestDelivery}
    >
      <SectionHeader>Pick up and Delivery</SectionHeader>
      <InnerWrapper
        className={
          componentState === EarliestDeliveryState.DesktopNoCars
            ? 'inline'
            : 'gap-24'
        }
      >
        <PickupAndDelivery componentState={componentState} />
        {componentState === EarliestDeliveryState.DesktopCars ? (
          <SimilarVehicles vehicles={vehicles} />
        ) : null}
      </InnerWrapper>
      {componentState !== EarliestDeliveryState.DesktopCars && (
        <Shipping hasCars={hasCars} />
      )}
      {componentState === EarliestDeliveryState.MobileCars ? (
        <div className="mt-20 w-full bg-white m:mt-0">
          <SimilarVehicles vehicles={vehicles} />
        </div>
      ) : null}
      {isPartner ? (
        <DisclaimerText>
          This partner vehicle may be for sale elsewhere. Availability will be
          confirmed at time of purchase.
        </DisclaimerText>
      ) : null}
      {isConsignment && <DisclaimerText>Under Consignment</DisclaimerText>}
    </Section>
  );
};

export default withErrorBoundary(
  EarliestDelivery,
  undefined,
  undefined,
  Sections.EarliestDelivery
);
